<template>
  <div class="flex flex-col gap-5" data-testid="project-settings-general">
    <label class="flex flex-col gap-1.5">
      <span class="text-[13px] font-medium text-[var(--color-dark,#1A1A1A)]">
        {{ $t('UserProjects.settings.general.name') }}
      </span>
      <InputText
        :model-value="modelValue.name"
        class="project-settings-field w-full"
        :disabled="!canManage"
        data-testid="project-settings-name"
        @update:model-value="patch({ name: $event })"
      />
    </label>

    <label class="flex flex-col gap-1.5">
      <span class="text-[13px] font-medium text-[var(--color-dark,#1A1A1A)]">
        {{ $t('UserProjects.settings.general.startDate') }}
      </span>
      <InputText
        type="date"
        :model-value="modelValue.startDate"
        class="project-settings-field w-full"
        :disabled="!canManage"
        data-testid="project-settings-start-date"
        @update:model-value="patch({ startDate: $event })"
      />
    </label>

    <label class="flex flex-col gap-1.5">
      <span class="text-[13px] font-medium text-[var(--color-dark,#1A1A1A)]">
        {{ $t('UserProjects.settings.general.description') }}
      </span>
      <Textarea
        :model-value="modelValue.description"
        rows="3"
        class="project-settings-field w-full"
        :disabled="!canManage"
        data-testid="project-settings-description"
        @update:model-value="patch({ description: $event })"
      />
    </label>

    <div>
      <div class="flex items-center justify-between mb-1.5">
        <span class="text-[13px] font-medium text-[var(--color-dark,#1A1A1A)]">
          {{ $t('UserProjects.settings.general.ppcTarget') }}
        </span>
        <div class="flex items-center gap-2">
          <InputText
            type="number"
            min="50"
            max="100"
            step="5"
            class="project-settings-field project-settings-field--ppc"
            :model-value="modelValue.ppcTargetPercent"
            :disabled="!canManage"
            data-testid="project-settings-ppc-target"
            @update:model-value="onPpcInput"
          />
          <span class="text-[13px] font-medium text-[var(--color-dark,#1A1A1A)]">%</span>
        </div>
      </div>
      <input
        type="range"
        min="50"
        max="100"
        step="5"
        class="w-full accent-[var(--color-orange,#e4821a)]"
        :value="modelValue.ppcTargetPercent"
        :disabled="!canManage"
        @input="onPpcInput($event.target.value)"
      >
      <p class="text-[13px] text-[#6B6B6B] mt-1 m-0">
        {{ $t('UserProjects.settings.general.ppcGap', {
          current: currentPpc ?? '—',
          gap: formatGap,
        }) }}
      </p>
    </div>

    <label class="flex flex-col gap-1.5">
      <span class="text-[13px] font-medium text-[var(--color-dark,#1A1A1A)]">
        {{ $t('UserProjects.settings.general.closeWeekday') }}
      </span>
      <Select
        :model-value="modelValue.commitmentCloseWeekday"
        :options="weekdayOptions"
        option-label="label"
        option-value="value"
        class="project-settings-field w-full"
        :disabled="!canManage"
        data-testid="project-settings-weekday"
        @update:model-value="patch({ commitmentCloseWeekday: $event })"
      />
    </label>

    <div class="border-t border-[var(--border-color,#E5E5E5)]" />

    <div
      v-if="canManage"
      class="p-4 rounded-[10px] border border-[#FCA5A5]"
      data-testid="project-settings-danger"
    >
      <p class="text-[14px] font-semibold text-[#991B1B] mb-1 m-0">
        {{ $t('UserProjects.settings.general.archiveTitle') }}
      </p>
      <p class="text-[13px] text-[#6B6B6B] mb-3 m-0">
        {{ $t('UserProjects.settings.general.archiveHint') }}
      </p>
      <button
        type="button"
        class="project-settings-danger-btn"
        data-testid="project-settings-close-project"
        @click="$emit('request-closure')"
      >
        {{ $t('UserProjects.settings.general.closeProject') }}
      </button>
    </div>
  </div>
</template>

<script>
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import Select from 'primevue/select';

export default {
  name: 'ProjectSettingsGeneralTab',
  components: { InputText, Textarea, Select },
  props: {
    modelValue: { type: Object, required: true },
    currentPpc: { type: Number, default: null },
    ppcGap: { type: Number, default: null },
    canManage: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'request-closure'],
  computed: {
    weekdayOptions() {
      return [0, 1, 2, 3, 4].map((value) => ({
        value,
        label: this.$t(`UserProjects.settings.weekdays.${value}`),
      }));
    },
    formatGap() {
      if (typeof this.ppcGap !== 'number') return '—';
      return this.ppcGap >= 0 ? `+${this.ppcGap}` : String(this.ppcGap);
    },
  },
  methods: {
    patch(partial) {
      this.$emit('update:modelValue', { ...this.modelValue, ...partial });
    },
    onPpcInput(raw) {
      const value = Math.min(100, Math.max(50, parseInt(raw, 10) || 50));
      this.patch({ ppcTargetPercent: value });
    },
  },
};
</script>

<style scoped>
.project-settings-danger-btn {
  padding: 0.5rem 1rem;
  border: 1.5px solid #ef4444;
  border-radius: 10px;
  background: transparent;
  color: #ef4444;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s ease;
}

.project-settings-danger-btn:hover {
  background: rgba(239, 68, 68, 0.05);
}
</style>
